<!-- modules/membership/PointsHistory.vue -->
<script setup>
import { ref, onMounted, computed } from 'vue';
import { useMembershipStore } from '@/stores/membership';
import DashboardCard from '@/components/common/DashboardCard.vue';
import Swal from 'sweetalert2';


const membershipStore = useMembershipStore();
const loading = ref(false);
const currentPage = ref(1);
const perPage = ref(10);

// Obtener historial de puntos
const pointsHistory = computed(() => membershipStore.pointsHistory);

// Total de puntos acumulados
const totalPoints = computed(() => membershipStore.totalPoints);

// Formatear fecha
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-ES', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Cargar historial
const loadHistory = async (page = 1) => {
  loading.value = true;
  try {
    await membershipStore.fetchPointsHistory(page, perPage.value);
    currentPage.value = page;
  } catch (error) {
    console.error("Error al cargar historial de puntos:", error);
    Swal.fire({
      icon: 'error',
      title: 'Error al cargar historial de puntos',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      text: error.response?.data?.message || 'Ocurrió un error al cargar el historial de puntos'
    });
  } finally {
    loading.value = false;
  }
};

// Cambiar página
const changePage = (page) => {
  loadHistory(page);
};

// Cargar datos al montar el componente
onMounted(async () => {
  await membershipStore.fetchMembershipInfo();
  loadHistory();
});

// Mapeo de tipos de acción a nombres legibles
const actionTypeNames = {
  'daily_login': 'Login Diario',
  'event_registration': 'Registro en Evento',
  'unregister': 'Cancelar Registro en Evento',
  'event_attendance': 'Asistencia a Evento',
  'profile_completion': 'Completar Perfil',
  'refer_friend': 'Referir Amigo',
  'social_share': 'Compartir en Redes Sociales'
};

// Obtener nombre legible para el tipo de acción
const getActionName = (actionType) => {
  return actionTypeNames[actionType] || actionType;
};
</script>

<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex justify-between items-center">
      <h1 class="text-2xl font-bold">Historial de Puntos</h1>
      <div class="text-right">
        <p class="text-sm text-gray-600">Puntos Acumulados</p>
        <p class="font-bold text-lg">{{ totalPoints }} pts</p>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="flex justify-center p-10">
      <div class="spinner"></div>
    </div>

    <div v-else>
      <!-- Points history table -->
      <DashboardCard>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actividad
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Puntos
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="(item, index) in pointsHistory.data" :key="index">
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ formatDate(item.created_at) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-medium text-gray-900">
                    {{ getActionName(item.action_type) || getActionName(item.metadata?.action || '') }}
                  </div>
                  <div v-if="item.metadata && Object.keys(item.metadata).length > 0" class="text-xs text-gray-500">
                    {{ item.metadata?.event_name || item.metadata?.description ||  '' }}
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span  v-if="item.points_earned > 0" class="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                    +{{ item.points_earned }}
                  </span>
                  <span  v-if="item.points_earned <= 0" class="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                    +{{ item.points_earned }}
                  </span>
                                  </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <span 
                    :class="[
                      'px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full',
                      item.is_validated 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    ]"
                  >
                    {{ item.is_validated ? 'Validado' : 'Pendiente' }}
                  </span>
                </td>
              </tr>

              <!-- Empty state -->
              <tr v-if="!pointsHistory.data || pointsHistory.data.length === 0">
                <td colspan="4" class="px-6 py-10 text-center text-gray-500">
                  No hay registros de actividad de puntos
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="px-6 py-3 flex items-center justify-between border-t border-gray-200" v-if="pointsHistory.meta">
          <div class="flex-1 flex justify-between sm:hidden">
            <button 
              @click="changePage(pointsHistory.meta.current_page - 1)"
              :disabled="pointsHistory.meta.current_page <= 1"
              class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              :class="{ 'opacity-50 cursor-not-allowed': pointsHistory.meta.current_page <= 1 }"
            >
              Anterior
            </button>
            <button 
              @click="changePage(pointsHistory.meta.current_page + 1)"
              :disabled="pointsHistory.meta.current_page >= pointsHistory.meta.last_page"
              class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              :class="{ 'opacity-50 cursor-not-allowed': pointsHistory.meta.current_page >= pointsHistory.meta.last_page }"
            >
              Siguiente
            </button>
          </div>
          <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p class="text-sm text-gray-700">
                Mostrando
                <span class="font-medium">{{ pointsHistory.meta.from || 0 }}</span>
                a
                <span class="font-medium">{{ pointsHistory.meta.to || 0 }}</span>
                de
                <span class="font-medium">{{ pointsHistory.meta.total }}</span>
                resultados
              </p>
            </div>
            <div>
              <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  @click="changePage(pointsHistory.meta.current_page - 1)"
                  :disabled="pointsHistory.meta.current_page <= 1"
                  class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  :class="{ 'opacity-50 cursor-not-allowed': pointsHistory.meta.current_page <= 1 }"
                >
                  <span class="sr-only">Anterior</span>
                  <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                </button>
                
                <button
                  v-for="page in pointsHistory.meta.last_page" 
                  :key="page"
                  @click="changePage(page)"
                  :class="[
                    'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                    page === pointsHistory.meta.current_page
                      ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                  ]"
                >
                  {{ page }}
                </button>
                
                <button
                  @click="changePage(pointsHistory.meta.current_page + 1)"
                  :disabled="pointsHistory.meta.current_page >= pointsHistory.meta.last_page"
                  class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  :class="{ 'opacity-50 cursor-not-allowed': pointsHistory.meta.current_page >= pointsHistory.meta.last_page }"
                >
                  <span class="sr-only">Siguiente</span>
                  <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      </DashboardCard>
    </div>
  </div>
</template>

<style scoped>
.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid #3b82f6;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>